import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// === Helpers ===
function orgFilter(query: any, orgId: string) {
  if (orgId && orgId !== 'all') {
    return query.eq('organization_id', orgId);
  }
  return query;
}

// ===================== AI Tool Licenses =====================
export function useToolLicenses(orgId: string) {
  return useQuery({
    queryKey: ['ai_tool_licenses', orgId],
    queryFn: async () => {
      let q = supabase.from('ai_tool_licenses').select('*').order('tool_name');
      q = orgFilter(q, orgId);
      const { data, error } = await q;
      if (error) throw error;
      return data || [];
    },
  });
}

export function useCreateToolLicense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (row: any) => {
      const { error } = await supabase.from('ai_tool_licenses').insert(row);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai_tool_licenses'] }); toast.success('Herramienta agregada'); },
    onError: (e: any) => toast.error(e.message),
  });
}

export function useUpdateToolLicense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...row }: any) => {
      const { error } = await supabase.from('ai_tool_licenses').update(row).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai_tool_licenses'] }); toast.success('Herramienta actualizada'); },
    onError: (e: any) => toast.error(e.message),
  });
}

export function useDeleteToolLicense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('ai_tool_licenses').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai_tool_licenses'] }); toast.success('Herramienta eliminada'); },
    onError: (e: any) => toast.error(e.message),
  });
}

// ===================== AI Models =====================
export function useAIModels(orgId: string) {
  return useQuery({
    queryKey: ['ai_models', orgId],
    queryFn: async () => {
      let q = supabase.from('ai_models').select('*').order('name');
      q = orgFilter(q, orgId);
      const { data, error } = await q;
      if (error) throw error;
      return data || [];
    },
  });
}

export function useCreateAIModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (row: any) => {
      const { error } = await supabase.from('ai_models').insert(row);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai_models'] }); toast.success('Modelo agregado'); },
    onError: (e: any) => toast.error(e.message),
  });
}

export function useUpdateAIModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...row }: any) => {
      const { error } = await supabase.from('ai_models').update(row).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai_models'] }); toast.success('Modelo actualizado'); },
    onError: (e: any) => toast.error(e.message),
  });
}

export function useDeleteAIModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('ai_models').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai_models'] }); toast.success('Modelo eliminado'); },
    onError: (e: any) => toast.error(e.message),
  });
}

// ===================== Software Data Sources =====================
export function useDataSources(orgId: string) {
  return useQuery({
    queryKey: ['software_data_sources', orgId],
    queryFn: async () => {
      let q = supabase.from('software_data_sources').select('*').order('name');
      q = orgFilter(q, orgId);
      const { data, error } = await q;
      if (error) throw error;
      return data || [];
    },
  });
}

export function useCreateDataSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (row: any) => {
      const { error } = await supabase.from('software_data_sources').insert(row);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['software_data_sources'] }); toast.success('Fuente de datos agregada'); },
    onError: (e: any) => toast.error(e.message),
  });
}

export function useUpdateDataSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...row }: any) => {
      const { error } = await supabase.from('software_data_sources').update(row).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['software_data_sources'] }); toast.success('Fuente actualizada'); },
    onError: (e: any) => toast.error(e.message),
  });
}

export function useDeleteDataSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('software_data_sources').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['software_data_sources'] }); toast.success('Fuente eliminada'); },
    onError: (e: any) => toast.error(e.message),
  });
}

// ===================== AI Own Solutions =====================
export function useOwnSolutions(orgId: string) {
  return useQuery({
    queryKey: ['ai_own_solutions', orgId],
    queryFn: async () => {
      let q = supabase.from('ai_own_solutions').select('*').order('name');
      q = orgFilter(q, orgId);
      const { data, error } = await q;
      if (error) throw error;
      return data || [];
    },
  });
}

export function useCreateOwnSolution() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (row: any) => {
      const { data, error } = await supabase.from('ai_own_solutions').insert(row).select('id').single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai_own_solutions'] }); toast.success('Solución agregada'); },
    onError: (e: any) => toast.error(e.message),
  });
}

export function useUpdateOwnSolution() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...row }: any) => {
      const { error } = await supabase.from('ai_own_solutions').update(row).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai_own_solutions'] }); toast.success('Solución actualizada'); },
    onError: (e: any) => toast.error(e.message),
  });
}

export function useDeleteOwnSolution() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('ai_own_solutions').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai_own_solutions'] }); qc.invalidateQueries({ queryKey: ['solution_projects'] }); toast.success('Solución eliminada'); },
    onError: (e: any) => toast.error(e.message),
  });
}

// ===================== Solution Projects (many-to-many) =====================
export function useSolutionProjects(solutionId: string | null) {
  return useQuery({
    queryKey: ['solution_projects', solutionId],
    enabled: !!solutionId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('solution_projects')
        .select('project_id')
        .eq('solution_id', solutionId!);
      if (error) throw error;
      return (data || []).map((r: any) => r.project_id as string);
    },
  });
}

export function useSolutionProjectsBatch(solutionIds: string[]) {
  return useQuery({
    queryKey: ['solution_projects_batch', solutionIds],
    enabled: solutionIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('solution_projects')
        .select('solution_id, project_id')
        .in('solution_id', solutionIds);
      if (error) throw error;
      const map: Record<string, string[]> = {};
      for (const row of data || []) {
        if (!map[row.solution_id]) map[row.solution_id] = [];
        map[row.solution_id].push(row.project_id);
      }
      return map;
    },
  });
}

export function useSyncSolutionProjects() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ solutionId, projectIds }: { solutionId: string; projectIds: string[] }) => {
      // Delete existing
      const { error: delError } = await supabase
        .from('solution_projects')
        .delete()
        .eq('solution_id', solutionId);
      if (delError) throw delError;
      // Insert new
      if (projectIds.length > 0) {
        const rows = projectIds.map(pid => ({ solution_id: solutionId, project_id: pid }));
        const { error: insError } = await supabase.from('solution_projects').insert(rows);
        if (insError) throw insError;
      }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['solution_projects'] }); qc.invalidateQueries({ queryKey: ['solution_projects_batch'] }); },
    onError: (e: any) => toast.error(e.message),
  });
}

// ===================== Org Projects (for solution linking) =====================
export function useOrgProjects(orgId: string) {
  return useQuery({
    queryKey: ['org_projects_for_solutions', orgId],
    enabled: !!orgId && orgId !== 'all',
    queryFn: async () => {
      // Get projects from users in this org
      const { data, error } = await supabase
        .from('participant_projects')
        .select('id, name, status, user_id, profiles!participant_projects_user_id_fkey!inner(organization_id, full_name)')
        .eq('profiles.organization_id', orgId)
        .order('name');
      if (error) throw error;
      return data || [];
    },
  });
}

// ===================== Enterprise Software =====================
export function useEnterpriseSoftware(orgId: string) {
  return useQuery({
    queryKey: ['enterprise_software', orgId],
    queryFn: async () => {
      let q = supabase.from('enterprise_software').select('*').order('name');
      q = orgFilter(q, orgId);
      const { data, error } = await q;
      if (error) throw error;
      return data || [];
    },
  });
}

export function useCreateEnterpriseSoftware() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (row: any) => {
      const { error } = await supabase.from('enterprise_software').insert(row);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['enterprise_software'] }); toast.success('Software agregado'); },
    onError: (e: any) => toast.error(e.message),
  });
}

export function useUpdateEnterpriseSoftware() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...row }: any) => {
      const { error } = await supabase.from('enterprise_software').update(row).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['enterprise_software'] }); toast.success('Software actualizado'); },
    onError: (e: any) => toast.error(e.message),
  });
}

export function useDeleteEnterpriseSoftware() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('enterprise_software').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['enterprise_software'] }); toast.success('Software eliminado'); },
    onError: (e: any) => toast.error(e.message),
  });
}
